<template>
  <v-card outlined class="mb-3">
    <v-card-text>
      <!-- Status indicators -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-chip
            v-if="!device.DeviceId || !device.MerchantId"
            color="error"
            small
            class="mr-2"
          >
            <v-icon left small>mdi-alert</v-icon>
            Missing required fields
          </v-chip>

          <v-chip
            v-if="device.MerchantId && !device.hasValidMerchantId"
            color="error"
            small
            class="mr-2"
          >
            <v-icon left small>mdi-alert</v-icon>
            Invalid Merchant ID
          </v-chip>

          <v-chip
            v-if="device.hasCsvStallholder && !device.StallholderId"
            color="warning"
            small
            class="mr-2"
          >
            <v-icon left small>mdi-alert</v-icon>
            Stallholder "{{ device.Stallholder }}" not found
          </v-chip>

          <v-chip
            v-if="device.hasCsvStall && !device.StallId"
            color="warning"
            small
            class="mr-2"
          >
            <v-icon left small>mdi-alert</v-icon>
            Stall "{{ device.Stall }}" not found
          </v-chip>

          <v-chip
            v-if="device.hasCsvTerminal && !device.TerminalId"
            color="warning"
            small
            class="mr-2"
          >
            <v-icon left small>mdi-alert</v-icon>
            Terminal "{{ device.Terminal }}" not found
          </v-chip>

          <v-chip
            v-if="device.hasCsvUser && !device.UserId"
            color="warning"
            small
            class="mr-2"
          >
            <v-icon left small>mdi-alert</v-icon>
            User "{{ device.User }}" not found
          </v-chip>

          <v-chip v-if="isFullyMapped" color="success" small class="mr-2">
            <v-icon left small>mdi-check-all</v-icon>
            Fully mapped
          </v-chip>

          <v-chip
            v-if="isReadyToSave && !isFullyMapped"
            color="primary"
            small
            class="mr-2"
          >
            <v-icon left small>mdi-check</v-icon>
            Ready to save
          </v-chip>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="device.DeviceId"
            label="Device ID"
            dense
            outlined
            :rules="[(v) => !!v || 'Device ID is required']"
            @input="updateDevice"
          ></v-text-field>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="device.MerchantId"
            label="Merchant ID"
            dense
            outlined
            :rules="merchantIdRules"
            :error="device.MerchantId && !device.hasValidMerchantId"
            :error-messages="
              device.MerchantId && !device.hasValidMerchantId
                ? ['Merchant ID must be a valid number']
                : []
            "
            @input="updateMerchantId"
          ></v-text-field>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-combobox
            v-model="device.Authorised"
            :items="authorisedTypes"
            item-text="name"
            :return-object="false"
            label="Device Authorised"
            dense
            outlined
            @change="updateDevice"
          ></v-combobox>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            :items="allStallHolders"
            v-model="selectedStallholder"
            item-text="StallholderName"
            return-object
            dense
            outlined
            label="Stallholder"
            @change="changeStallholder"
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title>
                  <v-icon small color="red lighten-1">mdi-information</v-icon>
                  No Stallholders found
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            :items="stalls"
            v-model="selectedStall"
            item-text="StallName"
            return-object
            dense
            outlined
            label="Stall"
            @change="changeStall"
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title>
                  <v-icon small color="red lighten-1">mdi-information</v-icon>
                  No Stalls found
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            :items="terminals"
            v-model="selectedTerminal"
            item-text="TerminalNumber"
            return-object
            dense
            outlined
            label="Terminal"
            @change="changeTerminal"
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title>
                  <v-icon small color="red lighten-1">mdi-information</v-icon>
                  No Terminals found
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            :items="terminalUsers"
            v-model="selectedUser"
            item-text="TerminalUserName"
            return-object
            dense
            outlined
            label="User"
            @change="changeUser"
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title>
                  <v-icon small color="red lighten-1">mdi-information</v-icon>
                  No Users found
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="d-flex align-center">
          <v-btn color="error" small icon @click="$emit('remove', index)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "DeviceRow",
  props: {
    device: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },

  data: () => ({
    selectedStallholder: {},
    selectedStall: {},
    selectedTerminal: {},
    selectedUser: {},
    stalls: [],
    terminals: [],
    terminalUsers: [],
    authorisedTypes: [
      { id: "Yes", name: "Yes" },
      { id: "No", name: "No" },
    ],
  }),

  computed: {
    ...mapState("filter", ["allStallHolders", "allTerminalUsers"]),
    isFullyMapped() {
      return (
        this.device.DeviceId &&
        this.device.MerchantId &&
        this.device.hasValidMerchantId &&
        // Either no CSV mapping data was provided, OR all provided CSV data was successfully mapped
        ((!this.device.hasCsvStallholder &&
          !this.device.hasCsvStall &&
          !this.device.hasCsvTerminal &&
          !this.device.hasCsvUser) ||
          ((!this.device.hasCsvStallholder || this.device.StallholderId) &&
            (!this.device.hasCsvStall || this.device.StallId) &&
            (!this.device.hasCsvTerminal || this.device.TerminalId) &&
            (!this.device.hasCsvUser || this.device.UserId)))
      );
    },

    isReadyToSave() {
      return (
        this.device.DeviceId &&
        this.device.MerchantId &&
        this.device.hasValidMerchantId
      );
    },
    merchantIdRules() {
      return [
        (v) => !!v || "Merchant ID is required",
        (v) =>
          this.isValidMerchantId(v) || "Merchant ID must be a valid number",
      ];
    },
  },

  created() {
    // Initialize selections based on CSV data
    this.$nextTick(() => {
      this.initializeSelections();
    });
  },

  watch: {
    selectedStallholder: function (val) {
      if (val && val.StallholderId) {
        this.stalls =
          this.allStallHolders.filter((element) => {
            return element.StallholderId === val.StallholderId;
          })[0]?.Stalls || [];

        this.terminalUsers = this.allTerminalUsers.filter((element) => {
          return element.StallholderId === val.StallholderId;
        });

        // Re-initialize stall mapping after stallholder is set
        this.initializeStallMapping();

        // Also initialize user mapping
        this.initializeUserMapping();
      }
    },

    selectedStall: function (val) {
      if (val && val.StallId) {
        this.terminals =
          this.stalls.filter((element) => {
            return element.StallId === val.StallId;
          })[0]?.Terminals || [];

        // Re-initialize terminal mapping after stall is set
        this.initializeTerminalMapping();
      }
    },

    // Watch for when allStallHolders and allTerminalUsers become available
    allStallHolders: {
      handler() {
        if (this.allStallHolders.length > 0) {
          this.initializeSelections();
        }
      },
      immediate: true,
    },

    allTerminalUsers: {
      handler() {
        if (this.allTerminalUsers.length > 0) {
          this.initializeSelections();
        }
      },
      immediate: true,
    },
  },

  methods: {
    initializeSelections() {
      // Try to find stallholder by name from CSV
      if (this.device.Stallholder && this.allStallHolders.length > 0) {
        const stallholder = this.allStallHolders.find((sh) => {
          const stallholderName = String(sh.StallholderName || "").trim();
          const csvStallholder = String(this.device.Stallholder || "").trim();
          return stallholderName.toLowerCase() === csvStallholder.toLowerCase();
        });
        if (stallholder) {
          this.selectedStallholder = stallholder;
          this.device.StallholderId = stallholder.StallholderId;

          // Initialize stalls and terminal users for this stallholder
          this.stalls = stallholder.Stalls || [];
          this.terminalUsers = this.allTerminalUsers.filter((element) => {
            return element.StallholderId === stallholder.StallholderId;
          });

          // Continue with stall mapping
          this.initializeStallMapping();

          // Also initialize user mapping
          this.initializeUserMapping();

          // Notify parent of stallholder mapping
          this.updateDevice();
        }
      }
    },

    initializeStallMapping() {
      // Try to find stall by name from CSV
      if (
        this.device.Stall &&
        this.selectedStallholder.StallholderId &&
        this.stalls.length > 0
      ) {
        const stall = this.stalls.find((s) => {
          const stallName = String(s.StallName || "").trim();
          const csvStall = String(this.device.Stall || "").trim();
          return stallName.toLowerCase() === csvStall.toLowerCase();
        });
        if (stall) {
          this.selectedStall = stall;
          this.device.StallId = stall.StallId;

          // Initialize terminals for this stall
          this.terminals = stall.Terminals || [];

          // Continue with terminal mapping
          this.initializeTerminalMapping();

          // Notify parent of stall mapping
          this.updateDevice();
        }
      }
    },

    initializeTerminalMapping() {
      // Try to find terminal by number from CSV
      if (
        this.device.Terminal &&
        this.selectedStall.StallId &&
        this.terminals.length > 0
      ) {
        const terminal = this.terminals.find((t) => {
          // Convert both values to strings for comparison
          const terminalNumber = String(t.TerminalNumber || "").trim();
          const csvTerminal = String(this.device.Terminal || "").trim();
          return terminalNumber.toLowerCase() === csvTerminal.toLowerCase();
        });
        if (terminal) {
          this.selectedTerminal = terminal;
          this.device.TerminalId = terminal.TerminalId;

          // Notify parent of terminal mapping
          this.updateDevice();
        }
      }
    },

    initializeUserMapping() {
      // Try to find user by name from CSV
      if (
        this.device.User &&
        this.selectedStallholder.StallholderId &&
        this.terminalUsers.length > 0
      ) {
        const user = this.terminalUsers.find((u) => {
          const userName = String(u.TerminalUserName || "").trim();
          const csvUser = String(this.device.User || "").trim();
          return userName.toLowerCase() === csvUser.toLowerCase();
        });
        if (user) {
          this.selectedUser = user;
          this.device.UserId = user.TerminalUserId;

          // Notify parent of user mapping
          this.updateDevice();
        }
      }
    },

    updateDevice() {
      this.$emit("update", this.device, this.index);
    },

    updateMerchantId(value) {
      this.device.MerchantId = value;
      this.device.hasValidMerchantId = this.isValidMerchantId(value);
      this.updateDevice();
    },

    isValidMerchantId(value) {
      // Check if the value is a valid number (including integer and decimal)
      if (!value || value.toString().trim() === "") {
        return false;
      }

      const trimmedValue = value.toString().trim();
      const numericValue = Number(trimmedValue);

      // Check if it's a valid number and not NaN
      return (
        !isNaN(numericValue) && isFinite(numericValue) && trimmedValue !== ""
      );
    },

    changeStallholder(obj) {
      this.selectedStallholder = obj;
      this.device.StallholderId = obj?.StallholderId;
      this.selectedStall = {};
      this.selectedTerminal = {};
      this.selectedUser = {};
      this.device.StallId = null;
      this.device.TerminalId = null;
      this.device.UserId = null;
      this.updateDevice();
    },

    changeStall(obj) {
      this.selectedStall = obj;
      this.device.StallId = obj?.StallId;
      this.selectedTerminal = {};
      this.selectedUser = {};
      this.device.TerminalId = null;
      this.device.UserId = null;
      this.updateDevice();
    },

    changeTerminal(obj) {
      this.selectedTerminal = obj;
      this.device.TerminalId = obj?.TerminalId;
      this.updateDevice();
    },

    changeUser(obj) {
      this.selectedUser = obj;
      this.device.UserId = obj?.TerminalUserId;
      this.updateDevice();
    },
  },
};
</script>
